import type { Job } from '@workflow/contracts'
import { ActionMenu, AvatarStack, Icons, ProgressRing, StatusLabel, Truncate, cn, type ActionMenuSection, type StatusHue, type StatusTone } from '@workflow/ui'
import { navigate } from '@/app/router'
import { formatDay } from '@/lib/format'
import { clientLabel } from '@/features/clients/clientStatus'
import {
  useSession,
  useArchiveJob,
  useDeleteJob,
  useDuplicateJob,
  useRestoreJob,
  useSetJobStatus,
} from '@/lib/api/hooks'
import { personName } from '@/lib/people'
import { HANDED_IN_SHORT, JOB_STATUS, formatHours, statusMoves } from './jobsStatus'
import { JobStatusGlyph } from './JobStatusCircle'

/** The hours a job's row reads: logged against its size, or what the crew holds without one. */
export function jobHours(job: Job): { text: string; value: number } {
  const logged = job.loggedHours
  if (job.estimatedHours !== null && job.estimatedHours > 0) {
    return { text: `${formatHours(logged)} / ${formatHours(job.estimatedHours)}`, value: logged / job.estimatedHours }
  }
  if (job.assignedHours > 0) return { text: `${formatHours(logged)} / ${formatHours(job.assignedHours)}`, value: logged / job.assignedHours }
  return { text: logged > 0 ? formatHours(logged) : '—', value: 0 }
}

/**
 * The tag a job wears: Archived; Ready To Close when every part is in with
 * its full hours and only the studio's close remains; Handed In Short when
 * every part is in but some came in under their hours; else its status.
 */
export function jobTag(job: Job): { label: string; tone: StatusTone; hue?: StatusHue } {
  if (job.archivedAt) return { label: 'Archived', tone: 'idle' }
  if (job.readyToComplete) return { label: 'Ready To Close', tone: 'done', hue: 'green' }
  if (job.handedInShort) return HANDED_IN_SHORT
  return JOB_STATUS[job.status]
}

/**
 * One row of work, wherever work is listed — Home and the Jobs page read the
 * same line, in the shape the player's own task list taught: a ring for how
 * far along the hours are, the name with its client and kind beneath, the
 * crew's faces, the state's tag standing beside the hours, and the three
 * dots holding every quick verb. A press on the row opens the job's quick
 * view; the full page is one more press from there.
 */
export function JobRow({ job, onOpen }: { job: Job; onOpen?: (job: Job) => void }) {
  const tag = jobTag(job)
  const hours = jobHours(job)
  const open = () => (onOpen ? onOpen(job) : navigate({ name: 'job', id: job.id }))
  // The day the work began; work that has not started has no date yet.
  const started = job.actualStart !== null ? formatDay(job.actualStart) : null

  return (
    <li className="border-b border-hairline last:border-0">
      <div className="-mx-2 flex items-center gap-1 rounded-2xl pr-1 transition-colors duration-fast hover:bg-tint">
        <button
          type="button"
          onClick={open}
          aria-label={`Open ${job.title}, ${tag.label}`}
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-2xl py-3.5 pl-2 text-left outline-none focus-visible:ring-1 focus-visible:ring-foreground sm:gap-3.5"
        >
          <ProgressRing
            value={Math.min(1, hours.value)}
            size={34}
            label={hours.text === '—' ? `${job.title} has no measured size` : `${hours.text} on ${job.title}`}
            className={cn('shrink-0', job.archivedAt && 'opacity-45')}
          />
          <span className="min-w-0 flex-1">
            <Truncate text={job.title} className="text-ui font-medium text-foreground" />
            <span className="mt-0.5 block truncate text-caption text-muted-foreground">
              {[clientLabel(job), job.typeName].join(' · ')}
              {started ? ` · ${started}` : null}
            </span>
          </span>
          {/* Faces, tag and hours each stand in a slot of fixed width, rendered
              for every row, so the dots and the figures fall on one vertical
              line down the list whatever the words or the crew's size. */}
          <span className="hidden w-[5.5rem] shrink-0 justify-end md:flex">
            {job.crew.length > 0 ? (
              <AvatarStack
                max={3}
                people={job.crew.map((face) => ({ key: face.assignmentId, name: personName(face), email: face.email, src: face.pictureUrl }))}
              />
            ) : null}
          </span>
          <span className="flex shrink-0 items-center gap-2 sm:gap-3">
            <StatusLabel className="whitespace-nowrap sm:w-[9.5rem]" tone={tag.tone} hue={tag.hue} label={tag.label} />
            <span className="hidden w-[5.5rem] justify-end sm:flex"><span className="whitespace-nowrap rounded-full bg-tint px-2 py-0.5 font-mono text-micro tabular-nums text-foreground">{hours.text}</span></span>
          </span>
        </button>
        <JobRowMenu job={job} />
      </div>
    </li>
  )
}

/**
 * The three dots, on the shared overflow: every quick thing a row can do
 * without opening — finish, copy, archiving, and
 * deleting with its ask-twice. The row itself must not answer for the
 * menu's clicks, so the menu stands inside a hand that stops them.
 */
export function JobRowMenu({ job, onDeleted }: { job: Job; onDeleted?: () => void }) {
  const isAdmin = useSession().data?.account?.role === 'admin'
  const move = useSetJobStatus()
  const duplicate = useDuplicateJob((copy) => navigate({ name: 'job', id: copy.id }))
  const archive = useArchiveJob()
  const restore = useRestoreJob()
  const remove = useDeleteJob(() => onDeleted?.())

  const busy = move.isPending || duplicate.isPending || archive.isPending || restore.isPending || remove.isPending
  const moves = statusMoves(job, isAdmin)
  const canComplete = moves.some((entry) => entry.target === 'completed')
  const canArchive = job.archivedAt === null && (job.status === 'completed' || job.status === 'removed' || job.status === 'cancelled')

  // The row itself opens the job, and editing is one press inside it, so the
  // menu carries only what the row cannot do on its own.
  const main: ActionMenuSection['items'] = []
  if (canComplete) {
    main.push({ key: 'complete', label: 'Mark As Complete', icon: <JobStatusGlyph status="completed" className="size-4" />, disabled: busy, onSelect: () => move.mutate({ id: job.id, status: 'completed' }) })
  }
  main.push({ key: 'duplicate', label: 'Duplicate', icon: <Icons.copy size="sm" />, disabled: busy, onSelect: () => duplicate.mutate({ id: job.id }) })
  if (canArchive) {
    main.push({ key: 'archive', label: 'Archive', icon: <Icons.archive size="sm" />, disabled: busy, onSelect: () => archive.mutate({ id: job.id }) })
  }
  if (job.archivedAt !== null) {
    main.push({ key: 'restore', label: 'Restore', icon: <Icons.restore size="sm" />, disabled: busy, onSelect: () => restore.mutate({ id: job.id }) })
  }

  return (
    <div className="justify-self-end" onClick={(event) => event.stopPropagation()}>
      <ActionMenu
        label={`Actions for ${job.title}`}
        sections={[
          { items: main },
          // Where the job can go from here — the moves the row's circle used to hold.
          ...(moves.some((entry) => entry.target !== 'completed') ? [{
            title: 'Move To',
            items: moves.filter((entry) => entry.target !== 'completed').map(({ target, label }) => ({
              key: `move-${target}`, label, icon: <JobStatusGlyph status={target} className="size-4" />, disabled: busy,
              onSelect: () => move.mutate({ id: job.id, status: target }),
            })),
          }] : []),
          {
            items: [{
              key: 'delete', label: 'Delete', icon: <Icons.trash size="sm" />, danger: true, disabled: busy,
              ask: { title: `Delete ${job.title}?`, detail: 'The job, its crew, notes and payments go with it. This cannot be undone.', confirmLabel: 'Delete' },
              onSelect: () => remove.mutate({ id: job.id, title: job.title }),
            }],
          },
        ]}
      />
    </div>
  )
}
